/**
 * PATH: packages/bills/src/components/PdfCanvasViewer.tsx
 * FILE_ID: BILLS-PKG-CMP-PDFCANVAS-001
 * PURPOSE: Render a bill's source PDF to <canvas> pages with a real,
 *          working zoom ladder + drag-to-pan — replacing the old
 *          `<iframe>#zoom=` branch whose zoom was a silent no-op on
 *          `blob:` URLs (operator 2026-06-19 "I tried to zoom it in… I
 *          couldn't").
 *
 * WHY: Chromium's built-in PDF viewer ignores the `#zoom=` fragment on
 *      `blob:` URLs and the iframe remounted on every change, so the
 *      zoom buttons / pinch did nothing. pdf.js gives us pixel-level
 *      control: we raster each page once at a crisp device scale, then
 *      scale the displayed canvas by the caller's `zoom` percent
 *      relative to fit-width — smooth zoom, no re-raster per step.
 *
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────┐   ┌──────────────────────┐
 *   │ blob / url    │ → │ pdfjs.getDocument│ → │ <canvas> per page,    │
 *   │ (bytes)       │   │  + page.render   │   │ CSS width = fit·zoom% │
 *   └──────────────┘   └─────────────────┘   └──────────────────────┘
 *
 * DEPENDENCIES:
 *   - pdfjs-dist (legacy build): browser-compat parse + raster. Same
 *     build + worker `?url` pattern as @quotech/email-hub PdfEditor, so
 *     it builds under every consuming app's Vite the same proven way.
 * RELATED:
 *   - DocumentViewer.tsx: owns zoom state + the scroll/pan wrapper.
 *   - zoomLadder.ts: DEFAULT_ZOOM is the fit-page baseline.
 */

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
// Legacy build for browser-compat (see email-hub/PdfEditor for the full
// rationale: pdfjs v5 default build assumes a bleeding-edge runtime).
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.mjs?url';
// Vite-bundled worker constructor. This is the ROBUST path: Vite emits the
// worker chunk and instantiates it with the correct module type, so it works
// even when the static host serves `.mjs` with the wrong Content-Type — which
// is exactly what was breaking `workerSrc` (a bare `?url`): the module worker
// failed to load, getDocument() hung, and every PDF fell back to the native
// <iframe> viewer. Preferring `workerPort` sidesteps server MIME entirely.
import PdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.mjs?worker';
import { recoverFromStaleDeploy } from '@quotech/ui';
import type { BillFieldRegion } from '../types';
import type { BillFieldKey } from './billFieldKeys';
import { Empty } from './viewerPrimitives';
import { DEFAULT_ZOOM } from './zoomLadder';
import { CapturePageLayer, extractTextBoxes, type Rect, type TextBox } from './pdfTextCapture';
import { PdfFieldPatch, PdfInlineFieldEditor } from './PdfInlineFieldEditor';
import {
  deriveBillFieldRegions,
  editedFieldValues,
  fieldRegionAtPoint,
  type BillRegionValues,
} from './pdfFieldRegions';
import { pdfViewerAssetsGone } from './staleDeployProbe';

// Fallback for any code path that spins pdf.js's own worker from a URL.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Fields printed in right-aligned numeric columns — amended-value patches
// keep that alignment so the corrected figure sits on the column edge.
const NUMERIC_FIELD_RE = /(quantity|unit_amount|line_total|subtotal|gst|tax|total|amount)$/;

// One shared worker across opens — avoids paying worker spin-up per bill.
let _sharedWorker: InstanceType<typeof pdfjsLib.PDFWorker> | null = null;
function sharedWorker(): InstanceType<typeof pdfjsLib.PDFWorker> | null {
  if (_sharedWorker && !(_sharedWorker as { destroyed?: boolean }).destroyed) {
    return _sharedWorker;
  }
  // Prefer a Vite-bundled worker instance passed as `port` (correct module
  // type, no server-MIME dependency). Fall back to the name-only constructor
  // (which relies on GlobalWorkerOptions.workerSrc) if that ever throws.
  try {
    _sharedWorker = new pdfjsLib.PDFWorker(
      { name: 'quotech-bills-pdf', port: new PdfjsWorker() } as unknown as ConstructorParameters<
        typeof pdfjsLib.PDFWorker
      >[0],
    );
  } catch {
    try {
      _sharedWorker = new pdfjsLib.PDFWorker(
        { name: 'quotech-bills-pdf' } as unknown as ConstructorParameters<
          typeof pdfjsLib.PDFWorker
        >[0],
      );
    } catch {
      _sharedWorker = null;
    }
  }
  return _sharedWorker;
}

interface RenderedPage {
  canvas: HTMLCanvasElement;
  /** Natural page size in CSS px (scale 1) — drives the fit-width ratio. */
  widthPt: number;
  heightPt: number;
  /** Text runs for "pick from PDF" (empty for image-only/scanned pages). */
  textBoxes: TextBox[];
}

interface Props {
  /** Source we already resolved upstream (blob preferred, else a URL). */
  source: { url: string; blob: Blob | null };
  /** Zoom percent from the shared ladder (DEFAULT_ZOOM = fit-page). */
  zoom: number;
  /** Scroll viewport from DocumentViewer — used to fit the whole page on open. */
  viewportRef?: RefObject<HTMLDivElement | null>;
  /** Bill id — re-parse when it changes. */
  cacheKey: string | number;
  /**
   * Per-field bounding boxes (1d). Empty until the extractor emits
   * coordinates — the overlay is simply absent then (cross-highlight is
   * the fallback). ``bbox`` is normalised 0..1 of the page; ``page`` is
   * 0-based.
   */
  regions?: BillFieldRegion[];
  /**
   * Extracted values (scalars + line items) used to SYNTHESIZE regions
   * client-side by matching each value against the PDF's text runs — the
   * fallback that makes sidebar→PDF highlight work while the extractor
   * emits no coordinates. Backend-provided ``regions`` win per field.
   */
  regionValues?: BillRegionValues | null;
  /**
   * The bill AS EXTRACTED (server state) — the anchor for region matching
   * when ``regionValues`` carries unsaved edits. Regions must keep matching
   * the PRINTED text even after the value is edited away from it; each
   * edited field then gets an "amended value" patch painted over its box
   * (the raster page itself can't change).
   */
  baseValues?: BillRegionValues | null;
  /** Field whose region(s) should be highlighted on the canvas. */
  activeField?: BillFieldKey | null;
  /** Click on a highlight box → report the field up for cross-highlight. */
  onRegionClick?: (field: BillFieldKey | null) => void;
  /**
   * "Pick from PDF" (p1e): when an ``onPick`` handler is provided the page
   * overlays an always-on pick layer (hover-reveal + click-to-pick). There is
   * no separate mode to arm — see CapturePageLayer. ``regionField`` is the
   * field whose highlight box the gesture started inside (null when the pick
   * landed outside every known region) so the caller can route an un-armed
   * pick straight to the right field.
   */
  onPick?: (
    text: string,
    regionField?: BillFieldKey | null,
    gesture?: 'click' | 'drag',
  ) => void;
  /**
   * In-place edit session (2026-07-24 "this edit function still does not
   * work"): when set, an editor floats over ``field``'s highlight box,
   * pre-filled with ``value``. ``nonce`` re-seeds the editor per open.
   */
  inlineEdit?: { field: BillFieldKey; value: string; nonce: number } | null;
  onInlineEditCommit?: (field: BillFieldKey, value: string) => void;
  onInlineEditCancel?: () => void;
  /** Page can pan (zoom > fit) — drives the overlay's off-text cursor. */
  pannable?: boolean;
}

/** Cap raster scale so a 400% zoom on a huge page can't OOM the tab. */
const MAX_RENDER_SCALE = 3;

/** Width at which a page fits entirely inside the scroll viewport. */
function fitPageWidth(
  viewportWidth: number,
  viewportHeight: number,
  pageWidth: number,
  pageHeight: number,
): number {
  if (viewportWidth <= 0 || pageWidth <= 0 || pageHeight <= 0) return viewportWidth;
  if (viewportHeight <= 0) return viewportWidth;
  const scale = Math.min(viewportWidth / pageWidth, viewportHeight / pageHeight);
  return pageWidth * scale;
}

async function loadBytes(source: Props['source']): Promise<Uint8Array> {
  if (source.blob) return new Uint8Array(await source.blob.arrayBuffer());
  // Cookie-auth path: pdf.js can't see our blob; fetch with credentials.
  const res = await fetch(source.url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to fetch PDF (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

export function PdfCanvasViewer({
  source,
  zoom,
  cacheKey,
  regions,
  regionValues,
  baseValues,
  activeField,
  onRegionClick,
  onPick,
  inlineEdit,
  onInlineEditCommit,
  onInlineEditCancel,
  pannable = false,
  viewportRef,
}: Props) {
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // When pdf.js can't render (worker failed to load / hung, or a parse
  // error) we fall back to the browser's NATIVE pdf viewer via an <iframe>
  // on the resolved blob/url — that path needs no pdf.js worker at all, so
  // the document still renders. Trade-off: no canvas zoom or "pick from
  // PDF" text layer in fallback mode, but a readable doc beats a spinner
  // that never resolves (operator 2026-07-01 "attachments not rendering").
  const [nativeFallback, setNativeFallback] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Measure the scroll viewport so DEFAULT_ZOOM means "fit whole page".
  useEffect(() => {
    const el = viewportRef?.current ?? containerRef.current;
    if (!el) return;
    const sync = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width > 0 || height > 0) setViewportSize({ width, height });
    };
    const ro = new ResizeObserver(() => sync());
    ro.observe(el);
    sync();
    return () => ro.disconnect();
  }, [viewportRef]);

  // Parse + raster every page once at a crisp device scale.
  useEffect(() => {
    let cancelled = false;
    let fellBack = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let task: { destroy: () => void; promise: Promise<any> } | null = null;
    setLoading(true);
    setError(null);
    setPages([]);
    setNativeFallback(false);

    // Deploy-rotation self-heal: pdf.js failing is USUALLY not a pdf.js
    // problem — a newer deploy deleted this tab's hashed assets, the worker
    // fetch 404'd, and getDocument() hung. The native <iframe> fallback then
    // reads fine but has NO text layer, NO highlight boxes, NO pick overlay
    // (operator 2026-07-25 "no bounding box nothing"). Probe our OWN build's
    // assets; if the server dropped them, hand off to the app's one-shot
    // reload (cooldown + banner + pre-reload auth refresh) so the tab comes
    // back on the current bundle. The native fallback still renders in the
    // meantime — and stays if the reload is on cooldown.
    const maybeRecoverStale = async () => {
      if (await pdfViewerAssetsGone(pdfWorkerUrl, import.meta.url)) {
        void recoverFromStaleDeploy();
      }
    };

    // Worker-stall guard: if pdf.js hasn't produced a document within the
    // budget, the worker almost certainly failed to load/instantiate (its
    // promise then never resolves OR rejects — a silent hang). Switch to the
    // native <iframe> renderer so the doc still shows instead of spinning
    // forever.
    fallbackTimer = setTimeout(() => {
      if (cancelled || fellBack) return;
      fellBack = true;
      // eslint-disable-next-line no-console
      console.error(
        '[PdfCanvasViewer] pdf.js did not render within 7s — falling back to the native PDF viewer. The pdf.js worker likely failed to load (check the Network tab for a failed pdf.worker*.mjs request).',
      );
      void maybeRecoverStale();
      setNativeFallback(true);
      setLoading(false);
    }, 7000);

    (async () => {
      try {
        const data = await loadBytes(source);
        if (cancelled || fellBack) return;
        const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const renderScale = Math.min(MAX_RENDER_SCALE, Math.max(1.5, dpr * 1.5));
        task = pdfjsLib.getDocument({ data, worker: sharedWorker() ?? undefined });
        const pdf = await task.promise;
        if (cancelled || fellBack) return;
        // Worker responded → cancel the stall guard; canvas path is healthy.
        if (fallbackTimer) clearTimeout(fallbackTimer);
        const out: RenderedPage[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: renderScale });
          const canvas = document.createElement('canvas');
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Browser does not support 2D canvas');
          await page.render({ canvas, canvasContext: ctx, viewport }).promise;
          // Pull the page's text runs (with positions) for "pick from PDF".
          // Best-effort: a scanned page returns [] and simply has no boxes.
          const textBoxes = await extractTextBoxes(page, base);
          out.push({ canvas, widthPt: base.width, heightPt: base.height, textBoxes });
          if (!cancelled) setPages([...out]);
        }
      } catch (e) {
        if (cancelled || fellBack) return;
        // eslint-disable-next-line no-console
        console.error('[PdfCanvasViewer] render failed', e);
        void maybeRecoverStale();
        // Prefer the native viewer over a dead-end error when we still have a
        // URL to hand the browser — most "errors" here (worker load failure,
        // odd PDF quirks) render fine natively.
        if (source.url) setNativeFallback(true);
        else setError(e instanceof Error ? e.message : 'Failed to render PDF');
      } finally {
        if (fallbackTimer) clearTimeout(fallbackTimer);
        if (!cancelled && !fellBack) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      try {
        task?.destroy();
      } catch {
        /* best-effort */
      }
    };
  }, [source, cacheKey]);

  // Fit-page baseline: DEFAULT_ZOOM shows the whole page in the viewport;
  // other zooms scale relative to that. Fit-width alone oversizes portrait
  // invoices (operator 2026-06-30 — "attachment seems too zoomed").
  const baselineWidth = useMemo(() => {
    const { width, height } = viewportSize;
    if (width <= 0) return 0;
    const first = pages[0];
    if (!first) return width;
    return fitPageWidth(width, height, first.widthPt, first.heightPt);
  }, [viewportSize, pages]);
  const displayWidth =
    baselineWidth > 0 ? baselineWidth * (zoom / DEFAULT_ZOOM) : undefined;

  // Client-side region synthesis: locate each extracted value in the pages'
  // text runs. Only kicks in for fields the backend gave no bbox for (today:
  // all of them — the extractor emits no coordinates). Anchored on the
  // AS-EXTRACTED values (baseValues) when available: an edited value no
  // longer matches the printed text, so matching on it would lose the box
  // exactly when we need it (to paint the amended value over the old text).
  const anchorValues = baseValues ?? regionValues;
  const derivedRegions = useMemo(() => {
    if (!anchorValues || pages.length === 0) return [];
    try {
      return deriveBillFieldRegions(pages.map((p) => p.textBoxes), anchorValues);
    } catch {
      return []; // best-effort — a matcher bug must never break the viewer
    }
  }, [pages, anchorValues]);

  const allRegions = useMemo(() => {
    const explicit = regions ?? [];
    const have = new Set(explicit.map((r) => r.field));
    return [...explicit, ...derivedRegions.filter((r) => !have.has(r.field))];
  }, [regions, derivedRegions]);

  // Fields whose live (draft) value differs from what the page prints →
  // painted over their region as an "amended value" patch, so an edit made
  // in the sidebar or the in-place editor IS visible on the document.
  const editedValues = useMemo(
    () =>
      editedFieldValues(
        baseValues ?? null,
        regionValues ?? null,
        allRegions.map((r) => r.field),
      ),
    [baseValues, regionValues, allRegions],
  );

  // Group regions by 0-based page so each PageCanvas only iterates its
  // own boxes. Recomputed only when the region set actually changes.
  const regionsByPage = useMemo(() => {
    const map = new Map<number, BillFieldRegion[]>();
    for (const r of allRegions) {
      const page = Number.isFinite(r.page) ? r.page : 0;
      const list = map.get(page);
      if (list) list.push(r);
      else map.set(page, [r]);
    }
    return map;
  }, [allRegions]);

  // Selecting a field in the sidebar scrolls the PDF to its highlight box.
  // Re-runs when regions land late (pages render incrementally), but scrolls
  // once per selection so ongoing page renders don't yank the viewport.
  const pagesHostRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledField = useRef<BillFieldKey | null>(null);
  useEffect(() => {
    if (!activeField) {
      lastScrolledField.current = null;
      return;
    }
    if (lastScrolledField.current === activeField) return;
    const host = pagesHostRef.current;
    if (!host) return;
    const el = host.querySelector<HTMLElement>(
      `[data-region-field="${CSS.escape(activeField)}"]`,
    );
    if (!el) return;
    lastScrolledField.current = activeField;
    el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  }, [activeField, allRegions]);

  // Native-viewer fallback: render the resolved blob/url in the browser's
  // built-in PDF viewer, which needs no pdf.js worker. `#toolbar=0&navpanes=0`
  // hides Chrome's chunky dark PDF chrome so it reads as a clean embedded doc
  // (our own header already provides Open-in-new-tab for full controls).
  if (nativeFallback && source.url) {
    const sep = source.url.includes('#') ? '&' : '#';
    return (
      <iframe
        src={`${source.url}${sep}toolbar=0&navpanes=0`}
        title="PDF document"
        className="h-full w-full rounded-lg border border-border bg-white"
      />
    );
  }
  if (error) {
    return (
      <Empty>
        <p>Could not render PDF.</p>
        <p className="mt-1 text-xs text-destructive">{error}</p>
      </Empty>
    );
  }

  return (
    <>
      {/*
        Width probe — ALWAYS the pane's usable width, independent of how wide
        the zoomed page gets. The pages container below is `w-max` so it can
        grow past the pane (that's what makes the left edge scroll-reachable),
        which means it can no longer be the thing we measure for fit-width:
        measuring it would feed its own zoomed width back into `displayWidth`
        and run away. A 0-height `w-full` probe gives a stable fit baseline.
      */}
      <div ref={containerRef} className="h-0 w-full" aria-hidden />
      {/*
        `w-max min-w-full` is the fix for "can't pan left when zoomed":
        - fit / small page → `min-w-full` makes the container fill the pane so
          `items-center` centres the page (looks tidy).
        - zoom > fit → `w-max` grows the container to the page's width, so the
          page starts at x=0 inside a container WIDER than the scroll pane.
          The scroll container's scrollWidth now includes the whole page and
          BOTH edges are reachable. A plain `w-full items-center` instead
          centres an over-wide child, pushing its left half past the pane's
          start edge where no scrollbar can ever reach it.
      */}
      <div ref={pagesHostRef} className="flex w-max min-w-full flex-col items-center gap-3">
        {pages.map((p, idx) => (
          <PageCanvas
            key={idx}
            page={p}
            displayWidth={displayWidth}
            fallbackWidth={baselineWidth}
            regions={regionsByPage.get(idx)}
            editedValues={editedValues}
            activeField={activeField ?? null}
            onRegionClick={onRegionClick}
            onPick={onPick}
            inlineEdit={inlineEdit}
            onInlineEditCommit={onInlineEditCommit}
            onInlineEditCancel={onInlineEditCancel}
            pannable={pannable}
          />
        ))}
        {loading && (
          <div className="py-6 text-center text-xs text-muted-foreground">
            {pages.length > 0
              ? `Rendering page ${pages.length + 1}…`
              : 'Loading document…'}
          </div>
        )}
      </div>
    </>
  );
}

/** Mounts a pre-rendered canvas node and scales it to the display width,
 *  overlaying any region highlight boxes for this page. */
function PageCanvas({
  page,
  displayWidth,
  fallbackWidth,
  regions,
  editedValues,
  activeField,
  onRegionClick,
  onPick,
  inlineEdit,
  onInlineEditCommit,
  onInlineEditCancel,
  pannable,
}: {
  page: RenderedPage;
  displayWidth: number | undefined;
  fallbackWidth: number;
  regions?: BillFieldRegion[];
  /** field → live value, for fields edited away from the printed text. */
  editedValues?: Map<string, string>;
  activeField?: BillFieldKey | null;
  onRegionClick?: (field: BillFieldKey | null) => void;
  onPick?: (
    text: string,
    regionField?: BillFieldKey | null,
    gesture?: 'click' | 'drag',
  ) => void;
  inlineEdit?: { field: BillFieldKey; value: string; nonce: number } | null;
  onInlineEditCommit?: (field: BillFieldKey, value: string) => void;
  onInlineEditCancel?: () => void;
  pannable?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const width = displayWidth ?? (fallbackWidth > 0 ? fallbackWidth : page.widthPt);
  const height = width * (page.heightPt / page.widthPt);

  // Amended-value patches: for each edited field with regions on this page,
  // the union bbox of its row boxes + the row count (sizes the patch font).
  const patches = useMemo(() => {
    if (!editedValues?.size || !regions?.length) return [];
    const byField = new Map<string, { rect: Rect; rows: number; value: string }>();
    for (const r of regions) {
      const value = editedValues.get(r.field);
      if (value === undefined) continue;
      const [x0, y0, x1, y1] = r.bbox;
      const b = {
        x0: Math.min(x0, x1),
        y0: Math.min(y0, y1),
        x1: Math.max(x0, x1),
        y1: Math.max(y0, y1),
      };
      const cur = byField.get(r.field);
      if (!cur) {
        byField.set(r.field, { rect: b, rows: 1, value });
      } else {
        cur.rect = {
          x0: Math.min(cur.rect.x0, b.x0),
          y0: Math.min(cur.rect.y0, b.y0),
          x1: Math.max(cur.rect.x1, b.x1),
          y1: Math.max(cur.rect.y1, b.y1),
        };
        cur.rows += 1;
      }
    }
    return [...byField.entries()].map(([field, p]) => ({ field, ...p }));
  }, [editedValues, regions]);

  // Highlight geometry the operator actually SEES. A patched field's
  // amended text wraps at its own points, not the printed rows' — keeping
  // the per-row boxes drew rings that cut the patch mid-line plus an empty
  // ring around a covered row ("bounded box got broken", 2026-07-25). So a
  // patched field collapses to ONE union region: the patch frames itself,
  // and hover/click hit-testing works on the same single box.
  const displayRegions = useMemo<BillFieldRegion[]>(() => {
    const src = regions ?? [];
    if (!patches.length) return src;
    const patched = new Set(patches.map((p) => p.field));
    return src.filter((r) => !patched.has(r.field));
  }, [regions, patches]);

  // Regions used for pointer hit-testing: unpatched row boxes + one union
  // box per patched field (so clicking the amended text still edits it).
  const hitRegions = useMemo<BillFieldRegion[]>(() => {
    if (!patches.length) return displayRegions;
    const pageIdx = regions?.[0]?.page ?? 0;
    return [
      ...displayRegions,
      ...patches.map((p) => ({
        field: p.field,
        page: pageIdx,
        bbox: [p.rect.x0, p.rect.y0, p.rect.x1, p.rect.y1] as [
          number,
          number,
          number,
          number,
        ],
      })),
    ];
  }, [displayRegions, patches, regions]);

  // The active field's highlight rect(s) on this page — the capture layer
  // uses them to skip its own hover ring inside an already-framed region
  // (no more stacked "double box").
  const activeRects = useMemo<Rect[]>(() => {
    if (!activeField || !hitRegions.length) return [];
    return hitRegions
      .filter((r) => r.field === activeField)
      .map((r) => {
        const [x0, y0, x1, y1] = r.bbox;
        return {
          x0: Math.min(x0, x1),
          y0: Math.min(y0, y1),
          x1: Math.max(x0, x1),
          y1: Math.max(y0, y1),
        };
      });
  }, [activeField, hitRegions]);

  // Picked text + start point → resolve which field region (if any) the
  // gesture began inside, so an un-armed click still lands in a field.
  const handleLayerPick = (
    text: string,
    at?: { x: number; y: number },
    gesture?: 'click' | 'drag',
  ) => {
    if (!onPick) return;
    const field = at ? (fieldRegionAtPoint(hitRegions, at.x, at.y) as BillFieldKey | null) : null;
    onPick(text, field, gesture);
  };

  // In-place editor anchor: the union bbox of the edited field's highlight
  // row(s) on THIS page (a wrapped description is several row boxes). Null
  // when the field has no region here — the editor simply isn't on this page.
  const editRect = useMemo<Rect | null>(() => {
    if (!inlineEdit || !regions?.length) return null;
    let acc: Rect | null = null;
    for (const r of regions) {
      if (r.field !== inlineEdit.field) continue;
      const [x0, y0, x1, y1] = r.bbox;
      const b = {
        x0: Math.min(x0, x1),
        y0: Math.min(y0, y1),
        x1: Math.max(x0, x1),
        y1: Math.max(y0, y1),
      };
      acc = acc
        ? {
            x0: Math.min(acc.x0, b.x0),
            y0: Math.min(acc.y0, b.y0),
            x1: Math.max(acc.x1, b.x1),
            y1: Math.max(acc.y1, b.y1),
          }
        : b;
    }
    return acc;
  }, [inlineEdit, regions]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const c = page.canvas;
    c.style.width = '100%';
    c.style.height = '100%';
    c.style.display = 'block';
    c.style.position = 'absolute';
    c.style.inset = '0';
    host.appendChild(c);
    return () => {
      if (c.parentNode === host) host.removeChild(c);
    };
  }, [page]);

  return (
    <div
      ref={hostRef}
      className="relative shrink-0 overflow-hidden rounded-md border border-border bg-white shadow-sm"
      style={{ width, height }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {patches.map((p) => (
        <PdfFieldPatch
          key={`patch-${p.field}`}
          field={p.field}
          rect={p.rect}
          rows={p.rows}
          value={p.value}
          pageHeight={height}
          alignRight={NUMERIC_FIELD_RE.test(p.field)}
          active={!!activeField && p.field === activeField}
        />
      ))}
      {displayRegions.map((r, i) => (
        <RegionBox
          key={`${r.field}-${i}`}
          region={r}
          active={!!activeField && r.field === activeField}
          onClick={onRegionClick}
        />
      ))}
      {onPick && page.textBoxes.length > 0 && (
        <CapturePageLayer
          boxes={page.textBoxes}
          onPick={handleLayerPick}
          pannable={pannable}
          activeRects={activeRects}
          regions={hitRegions}
          activeFieldKey={activeField}
        />
      )}
      {inlineEdit && editRect && onInlineEditCommit && (
        <PdfInlineFieldEditor
          key={inlineEdit.nonce}
          rect={editRect}
          value={inlineEdit.value}
          multiline={/\.description$/.test(inlineEdit.field)}
          onCommit={(v) => onInlineEditCommit(inlineEdit.field, v)}
          onCancel={() => onInlineEditCancel?.()}
        />
      )}
      {onPick && page.textBoxes.length === 0 && hovered && (
        // Scanned / image-only page: pdf.js has no text layer to pick from.
        // Only surfaced on hover so it never nags while reading.
        <div className="pointer-events-none absolute inset-x-0 top-2 z-30 flex justify-center">
          <span className="rounded-md bg-amber-500/90 px-2 py-1 text-[11px] font-medium text-white shadow">
            No selectable text on this page (scanned) — type the value manually
          </span>
        </div>
      )}
    </div>
  );
}

/** A single absolutely-positioned highlight box over the page canvas.
 *  Inactive boxes are faint hint outlines; the active one is solid. */
function RegionBox({
  region,
  active,
}: {
  region: BillFieldRegion;
  active: boolean;
  /** @deprecated Clicks are handled by CapturePageLayer above this node. */
  onClick?: (field: BillFieldKey | null) => void;
}) {
  const [x0, y0, x1, y1] = region.bbox;
  // Defensive: ignore a degenerate / out-of-range box rather than paint
  // a full-page rectangle (extractor coordinates are unverified).
  const lo = Math.max(0, Math.min(x0, x1));
  const top = Math.max(0, Math.min(y0, y1));
  const w = Math.min(1, Math.max(x0, x1)) - lo;
  const h = Math.min(1, Math.max(y0, y1)) - top;
  if (!(w > 0) || !(h > 0)) return null;

  return (
    <div
      data-region-field={region.field}
      title={region.field}
      aria-label={`Highlight ${region.field}`}
      className={
        active
          ? // Square-cornered marker wash + single 1px frame: reads as ONE
            // highlight even with the pick layer's hover fill on top
            // (operator 2026-07-24: "two bounded box… rounded corner i
            // dont want this").
            'pointer-events-none absolute z-10 border border-primary/80 bg-primary/15 transition-colors'
          : // Faint always-on outline (hover on this node is dead — the
            // CapturePageLayer sits above at z-20). Whole-field hover wash
            // still comes from the capture layer.
            'pointer-events-none absolute z-10 border border-primary/35 bg-primary/[0.04]'
      }
      style={{
        left: `${lo * 100}%`,
        top: `${top * 100}%`,
        width: `${w * 100}%`,
        height: `${h * 100}%`,
      }}
    />
  );
}
